import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, Upload, CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export const AsiaRegionPage = () => {
  const countries = [
    { name: 'Cambodia', slug: 'cambodia' },
    { name: 'Myanmar', slug: 'myanmar-pharma-nutra-registration' },
    { name: 'Sri Lanka', slug: 'sri-lanka-pharma-nutra-registration' },
    { name: 'Nepal', slug: 'nepal-pharma-nutra-registration' },
    { name: 'Mauritius', slug: 'mauritius-pharma-nutra-registration' },
    { name: 'Malaysia', slug: 'malaysia-pharma-nutra-registration' },
    { name: 'Bangladesh', slug: 'bangladesh-pharma-nutra-registration' },
    { name: 'Bhutan', slug: 'bhutan-pharma-nutra-registration' },
    { name: 'Maldives', slug: 'maldives-pharma-nutra-registration' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Asia Pharma Registration | ASEAN CTD Dossier Cambodia Myanmar Sri Lanka Bangladesh</title>
        <meta
          name="description"
          content="Expert regulatory affairs for pharmaceutical and nutraceutical registration across ASEAN and South Asia. CTD/ACTD dossiers for Cambodia, Myanmar, Sri Lanka, Malaysia, Bangladesh."
        />
        <meta name="keywords" content="asia pharma registration, ASEAN regulatory affairs, cambodia pharma registration, myanmar dossier, sri lanka regulatory, malaysia registration, bangladesh pharma, CTD dossier asia, supplement registration asia, pharma exports asia" />
        
        <meta property="og:title" content="Asia Pharma Registration | ASEAN & South Asia Regulatory Affairs" />
        <meta property="og:description" content="Complete dossier development and regulatory mapping for pharma and nutra products across ASEAN and South Asia markets." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6" style={{ backgroundColor: '#7090A0', color: 'white' }}>
            Asia Region
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            Regulatory Affairs in Asia
          </h1>
          <p className="text-2xl mb-4" style={{ color: '#505060' }}>
            Your partner for dossier development, regulatory mapping and multi-country submissions across ASEAN & South Asia.
          </p>
          <p className="text-xl mb-12" style={{ color: '#606070' }}>
            We simplify entry into some of the most diverse regulatory systems — from fast-growth markets like Cambodia & Myanmar to compliance-driven markets like Sri Lanka & Malaysia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('countries').scrollIntoView({ behavior: 'smooth' })} style={{ backgroundColor: '#7090A0', color: 'white' }} className="text-lg px-8 py-6">
              Explore Asian Markets
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" style={{ borderColor: '#7090A0', color: '#7090A0' }}>
                Book a Call
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Asia Matters */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why Asia Matters
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            Asia's nutraceutical and pharmaceutical markets are growing rapidly due to:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Expanding middle class',
              'Strong demand for OTC, supplements and generics',
              'High prevalence of metabolic disorders',
              'Flexible yet structured regulatory pathways in ASEAN'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: '#F0E0D0' }}>
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#7090A0' }} />
                <span className="text-lg" style={{ color: '#303040' }}>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xl mt-8 text-center font-medium" style={{ color: '#7090A0' }}>
            Asia is highly importer-driven, making it one of the easiest regions to scale for compliant manufacturers.
          </p>
        </div>
      </section>

      {/* Countries Covered */}
      <section id="countries" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Countries Covered
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map((country) => (
              <Link
                key={country.slug}
                to={`/country/${country.slug}`}
                className="group block p-6 bg-white rounded-2xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                style={{ borderLeft: '4px solid #7090A0' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold" style={{ color: '#303040' }}>{country.name}</span>
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" style={{ color: '#7090A0' }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            What TheApostrophe Does in Asia
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Complete CTD/ACTD dossier creation',
              'Supplement classification & claim review',
              'Ingredient acceptability check',
              'Local-language artwork & translations',
              'Regulatory mapping for each market',
              'Importer coordination',
              'Pre-submission checklists'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#7090A0' }} />
                <span className="text-lg" style={{ color: '#505060' }}>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Products */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Ideal Product Categories
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#7090A0' }}>Pharma</h3>
              <ul className="space-y-2">
                {['Chronic-care: diabetes, hypertension', 'Antibiotics (selected markets)', 'GI & hepatoprotective', 'Pediatric drops & syrups'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7090A0' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#7090A0' }}>Nutra</h3>
              <ul className="space-y-2">
                {['Metabolic & PCOS support', 'Joint health', 'Immunity boosters', 'Prenatal products', 'Liver/digestive formulations'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7090A0' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#303040' }}>
              Asia Registration FAQs
            </h2>
            <p className="text-lg" style={{ color: '#708080' }}>
              Common questions about ASEAN and South Asia pharmaceutical registration
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  What is ACTD format and which countries require it?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>ACTD (ASEAN Common Technical Document)</strong> is the dossier format used in ASEAN countries including <strong>Cambodia, Myanmar, Malaysia, and Sri Lanka</strong>. It follows a 5-module CTD structure but has ASEAN-specific Module 1 administrative requirements. We prepare ACTD-compliant dossiers aligned with each country's regulatory authority.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Which Asian countries offer the fastest approval timelines?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Cambodia</strong> offers one of the fastest timelines (3-6 months) with streamlined processes. <strong>Nepal and Sri Lanka</strong> typically take 6-12 months, while <strong>Malaysia</strong> can take 12-18 months due to more rigorous review. Timeline depends on dossier completeness and local importer efficiency.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Do Asian countries accept English-only labels?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  It varies by country. <strong>Cambodia, Sri Lanka, and Bangladesh</strong> generally accept English labels. <strong>Myanmar</strong> requires Burmese translations for key information. <strong>Malaysia</strong> requires Malay translations. We provide country-specific artwork compliance and translation services for all Asian markets.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #7090A0 0%, #80A0B0 100%)' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to enter ASEAN & South Asia with clarity?
          </h2>
          <p className="text-2xl mb-8 text-white/90">
            We build region-specific dossiers that pass smoothly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" className="text-lg px-8 py-6 bg-white hover:bg-white/90" style={{ color: '#7090A0' }}>
                Start Asia Expansion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                <Upload className="mr-2 h-5 w-5" />
                Upload Product List
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
